import { clearPortalSession, discardLegacyPortalSession, getPortalSession, getPortalSessionForAccount, setPortalSession } from "./portal/portalSession.js";
export { IMPERSONATION_STORAGE_KEY } from "./portal/portalSession.js";
export const IMPERSONATION_QUERY_EMAIL_KEY = "impersonate_email";
export const IMPERSONATION_QUERY_ADMIN_EMAIL_KEY = "impersonate_admin_email";
export const IMPERSONATION_QUERY_ADMIN_ACCOUNT_KEY = "impersonate_admin_account";

function normalizeEmail(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function summarizeAccount(account) {
  if (!account) {
    return {
      email: "",
      homeAccountId: "",
    };
  }

  return {
    email: normalizeEmail(
      account.username || account.idTokenClaims?.preferred_username || ""
    ),
    homeAccountId: account.homeAccountId || "",
  };
}

function normalizeStoredImpersonation(value) {
  if (!value || typeof value !== "object") {
    return null;
  }

  const impersonatedEmail = normalizeEmail(value.impersonatedEmail);
  const adminEmail = normalizeEmail(value.adminEmail);
  const adminHomeAccountId =
    typeof value.adminHomeAccountId === "string" ? value.adminHomeAccountId : "";

  if (!impersonatedEmail || (!adminEmail && !adminHomeAccountId)) {
    return null;
  }

  return {
    impersonatedEmail,
    adminEmail,
    adminHomeAccountId,
  };
}

export function readStoredImpersonation() {
  // Legacy API name: active impersonation now lives only in this document's memory.
  return getPortalSession().impersonation;
}

export function writeStoredImpersonation(value, portal = "grower") {
  const normalizedValue = normalizeStoredImpersonation(value);
  if (!normalizedValue) return null;
  const session = setPortalSession({
    portal,
    impersonation: normalizedValue,
    owner: { accountId: normalizedValue.adminHomeAccountId, email: normalizedValue.adminEmail },
  });
  return session ? normalizedValue : null;
}

export function clearStoredImpersonation() {
  if (getPortalSession().impersonation) clearPortalSession();
  return null;
}

export function doesImpersonationBelongToAccount(impersonation, account) {
  const normalizedImpersonation = normalizeStoredImpersonation(impersonation);

  if (!normalizedImpersonation) {
    return false;
  }

  const normalizedAccount = summarizeAccount(account);

  if (
    normalizedImpersonation.adminHomeAccountId &&
    normalizedAccount.homeAccountId &&
    normalizedImpersonation.adminHomeAccountId === normalizedAccount.homeAccountId
  ) {
    return true;
  }

  if (
    normalizedImpersonation.adminEmail &&
    normalizedAccount.email &&
    normalizedImpersonation.adminEmail === normalizedAccount.email
  ) {
    return true;
  }

  return false;
}

export function getImpersonationForAccount(account, storedImpersonation = null) {
  const impersonation = storedImpersonation || getPortalSessionForAccount(account).impersonation;

  if (!doesImpersonationBelongToAccount(impersonation, account)) {
    return null;
  }

  return normalizeStoredImpersonation(impersonation);
}

export function buildImpersonationState({ impersonatedEmail, account }) {
  const normalizedAccount = summarizeAccount(account);

  return normalizeStoredImpersonation({
    impersonatedEmail,
    adminEmail: normalizedAccount.email,
    adminHomeAccountId: normalizedAccount.homeAccountId,
  });
}

export function getInitialImpersonationState() {
  discardLegacyPortalSession();
  return readStoredImpersonation();
}
